import React, { useEffect, useState } from "react";
import "./AboutTab.css";
import supabase from "../../../supabaseClient";
import { CircularProgress, TextField, Select, MenuItem, FormControl, InputLabel, Switch, Button, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useAuth } from '../../../contexts/authContext';
import ReactSelect from "react-select";

const CATEGORY_OPTIONS = [
  { value: "Vocabulary", label: "Vocabulary" },
  { value: "Reading", label: "Reading" },
  { value: "Writing", label: "Writing" },
  { value: "Pronunciation", label: "Pronunciation" },
  { value: "Conversation (Speaking)", label: "Conversation (Speaking)" },
  { value: "Grammar", label: "Grammar" },
  { value: "Culture", label: "Culture" },
  { value: "Idioms", label: "Idioms" },
  { value: "Slang", label: "Slang" },
  { value: "Literature", label: "Literature" },
  { value: "Academic Writing", label: "Academic Writing" },
  { value: "Business Communication", label: "Business Communication" },
  { value: "Exam Preparation", label: "Exam Preparation" },
  { value: "Translation", label: "Translation" },
  { value: "Phonetics", label: "Phonetics" },
  { value: "Other", label: "Other" }
];

const LANGUAGE_OPTIONS = [
  "English", "Spanish", "French", "German", "Italian", "Portuguese", "Russian", "Chinese", "Japanese", "Korean", "Arabic", "Hindi", "Bengali", "Urdu", "Turkish", "Vietnamese", "Polish", "Dutch", "Greek", "Czech", "Swedish", "Finnish", "Norwegian", "Danish", "Hungarian", "Romanian", "Hebrew", "Thai", "Indonesian", "Malay", "Filipino", "Swahili", "Zulu", "Afrikaans", "Persian", "Ukrainian", "Serbian", "Croatian", "Slovak", "Bulgarian", "Slovenian", "Estonian", "Latvian", "Lithuanian", "Other"
];

const LEVEL_OPTIONS = [
  { value: "A1", label: "A1 (Beginner)" },
  { value: "A2", label: "A2 (Elementary)" },
  { value: "B1", label: "B1 (Intermediate)" },
  { value: "B2", label: "B2 (Upper Intermediate)" },
  { value: "C1", label: "C1 (Advanced)" },
  { value: "C2", label: "C2 (Proficient)" }
];

const DAYS_OF_WEEK = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

const AboutTab = ({ groupId, isMember, onJoinGroupSuccess }) => {
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editGroup, setEditGroup] = useState({});
  const [editSchedules, setEditSchedules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!groupId || !user) return;
    setLoading(true);
    setError(null);
    const fetchGroupData = async () => {
      try {
        const { data: groupData, error: groupError } = await supabase
          .from("groups")
          .select("id, name, description, language, level, category, recurring, frequency, created_by")
          .eq("id", groupId)
          .single();
        if (groupError) throw groupError;
        setGroup(groupData);
        setEditGroup({
          ...groupData,
          category: Array.isArray(groupData.category) ? groupData.category : (groupData.category ? [groupData.category] : [])
        });
        // Check admin: creator or group_members role
        let admin = false;
        if (user && groupData.created_by === user.id) {
          admin = true;
        } else {
          const { data: memberRows, error: memberError } = await supabase
            .from("group_members")
            .select("role")
            .eq("group_id", groupId)
            .eq("user_id", user.id)
            .single();
          if (!memberError && memberRows && memberRows.role && memberRows.role.toLowerCase() === "admin") {
            admin = true;
          }
        }
        setIsAdmin(admin);
        const { data: scheduleData, error: scheduleError } = await supabase
          .from("group_schedules")
          .select("id, day, time, duration")
          .eq("group_id", groupId);
        if (scheduleError) throw scheduleError;
        setSchedules(scheduleData || []);
        setEditSchedules(scheduleData || []);
      } catch (err) {
        setError(err.message || "Failed to fetch group info.");
      } finally {
        setLoading(false);
      }
    };
    fetchGroupData();
  }, [groupId, user]);

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditGroup((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCategoryChange = (selected) => {
    setEditGroup((prev) => ({
      ...prev,
      category: selected ? selected.map(opt => opt.value) : []
    }));
  };

  const handleLanguageChange = (e) => {
    setEditGroup((prev) => ({
      ...prev,
      language: e.target.value
    }));
  };

  const handleLevelChange = (e) => {
    setEditGroup((prev) => ({
      ...prev,
      level: e.target.value
    }));
  };

  const handleScheduleChange = (idx, field, value) => {
    setEditSchedules((prev) => prev.map((sch, i) => i === idx ? { ...sch, [field]: value } : sch));
  };

  const handleAddSchedule = () => {
    setEditSchedules((prev) => [...prev, { day: "", time: "", duration: 60 }]);
  };

  const handleRemoveSchedule = (idx) => {
    setEditSchedules((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Update group
      const { error: groupError } = await supabase
        .from("groups")
        .update({
          name: editGroup.name,
          description: editGroup.description,
          language: editGroup.language,
          level: editGroup.level,
          category: editGroup.category,
          recurring: editGroup.recurring,
        })
        .eq("id", groupId);
      if (groupError) throw groupError;
      // Update schedules: delete all and re-insert
      await supabase.from("group_schedules").delete().eq("group_id", groupId);
      for (const sch of editSchedules) {
        if (sch.day && sch.time && sch.duration) {
          await supabase.from("group_schedules").insert({
            group_id: groupId,
            day: sch.day,
            time: sch.time,
            duration: sch.duration,
          });
        }
      }
      setGroup({ ...editGroup });
      setSchedules([...editSchedules]);
      setEditMode(false);
    } catch (err) {
      setError(err.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleJoinGroup = async () => {
    if (!user || !group) return;
    try {
      const { error } = await supabase
        .from('group_members')
        .insert({
          group_id: group.id,
          user_id: user.id,
          role: 'member',
          joined_at: new Date().toISOString(),
        });
      if (error) throw error;
      if (onJoinGroupSuccess) onJoinGroupSuccess();
    } catch (err) {
      alert(err.message || 'Failed to join group.');
    }
  };

  if (loading) return (
    <div className="group-about-tab group-about-loading">
      <CircularProgress size={40} style={{ color: 'var(--primary)' }} />
      <p>Loading group information...</p>
    </div>
  );

  if (error) return (
    <div className="group-about-tab group-about-error">
      <h2>Error Loading Group</h2>
      <p>{error}</p>
    </div>
  );

  if (!group) return (
    <div className="group-about-tab group-about-empty">
      <h2>No Group Data</h2>
      <p>No group information is currently available.</p>
    </div>
  );

  // Helper to format time in 12-hour format
  const formatTime12Hour = (timeStr) => {
    if (!timeStr) return '';
    const [hour, minute] = timeStr.split(":");
    let h = parseInt(hour, 10);
    const m = minute.padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${m} ${ampm}`;
  };

  return (
    <div className="group-about-tab">
      {!editMode ? (
        <div className="group-about-display">
          <div className="group-about-header">
            <h2>{group.name}</h2>
            {isAdmin && (
              <IconButton aria-label="edit" onClick={() => setEditMode(true)} size="small" className="edit-btn">
                <EditIcon />
              </IconButton>
            )}
          </div>
          <div className="group-about-section">
            <strong>Description:</strong>
            <p>{group.description || 'No description provided.'}</p>
          </div>
          <div className="group-about-section">
            <strong>Language:</strong> {group.language}
          </div>
          <div className="group-about-section">
            <strong>Level:</strong> {LEVEL_OPTIONS.find(opt => opt.value === group.level)?.label || group.level}
          </div>
          <div className="group-about-section">
            <strong>Category:</strong> {Array.isArray(group.category) ? group.category.join(', ') : group.category}
          </div>
          <div className="group-about-section">
            <strong>Recurring:</strong>{' '}
            {group.recurring
              ? (group.frequency === 'weekly'
                  ? 'Weekly'
                  : group.frequency === 'biweekly'
                  ? 'Every 2 weeks'
                  : group.frequency === 'monthly'
                  ? 'Monthly'
                  : 'Yes')
              : 'No'}
          </div>
          <div className="group-about-section">
            <strong>Schedule:</strong>
            {schedules.length === 0 ? (
              <span> No schedule set.</span>
            ) : (
              <ul className="group-schedule-list">
                {schedules.map((sch, idx) => (
                  <li key={sch.id || idx}>
                    {sch.day} - {formatTime12Hour(sch.time)} ({sch.duration} min)
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* CTA for non-members: encourage them to join the group */}
          {!isMember && (
            <div className="group-about-cta">
              <h3>Interested in this group?</h3>
              <p>Join the group to access the forum, members, media, and shared files.</p>
              <Button
                variant="contained"
                color="primary"
                onClick={handleJoinGroup}
                style={{ marginTop: 8, background: '#3949ab', color: '#fff', borderRadius: 8 }}
              >
                Join Group
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="group-about-edit modern-edit-mode">
          <h2>Edit Group</h2>
          <div className="edit-form-row">
            <TextField
              label="Title"
              name="name"
              value={editGroup.name || ''}
              onChange={handleEditChange}
              fullWidth
              variant="outlined"
              className="edit-input"
              InputProps={{ style: { background: '#181f2a', color: '#fff', borderRadius: 8 } }}
              InputLabelProps={{ style: { color: '#9fa8da' } }}
            />
          </div>
          <div className="edit-form-row">
            <TextField
              label="Description"
              name="description"
              value={editGroup.description || ''}
              onChange={handleEditChange}
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              className="edit-input"
              InputProps={{ style: { background: '#181f2a', color: '#fff', borderRadius: 8 } }}
              InputLabelProps={{ style: { color: '#9fa8da' } }}
            />
          </div>
          <div className="edit-form-row">
            <FormControl fullWidth variant="outlined" className="edit-input">
              <InputLabel style={{ color: '#9fa8da' }}>Language</InputLabel>
              <Select
                label="Language"
                name="language"
                value={editGroup.language || ''}
                onChange={handleLanguageChange}
                style={{ background: '#181f2a', color: '#fff', borderRadius: 8 }}
                labelId="language-label"
              >
                {LANGUAGE_OPTIONS.map(lang => (
                  <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="edit-form-row">
            <FormControl fullWidth variant="outlined" className="edit-input">
              <InputLabel style={{ color: '#9fa8da' }}>Level</InputLabel>
              <Select
                label="Level"
                name="level"
                value={editGroup.level || ''}
                onChange={handleLevelChange}
                style={{ background: '#181f2a', color: '#fff', borderRadius: 8 }}
                labelId="level-label"
              >
                {LEVEL_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="edit-form-row">
            <label style={{ color: '#9fa8da', marginBottom: 4 }}>Category</label>
            <ReactSelect
              isMulti
              options={CATEGORY_OPTIONS}
              value={CATEGORY_OPTIONS.filter(opt => (editGroup.category || []).includes(opt.value))}
              onChange={handleCategoryChange}
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, background: '#181f2a', color: '#fff', borderRadius: 8, borderColor: '#3949ab' }),
                multiValue: (base) => ({ ...base, background: '#3949ab', color: '#fff' }),
                option: (base, state) => ({ ...base, background: state.isSelected ? '#3949ab' : '#181f2a', color: '#fff' })
              }}
            />
          </div>
          <div className="edit-form-row">
            <label style={{ color: '#9fa8da', marginRight: 12 }}>Recurring</label>
            <Switch
              checked={!!editGroup.recurring}
              onChange={e => setEditGroup(prev => ({ ...prev, recurring: e.target.checked }))}
              color="primary"
            />
          </div>
          <div className="edit-form-row">
            <FormControl fullWidth variant="outlined" className="edit-input">
              <InputLabel style={{ color: '#9fa8da' }}>Frequency</InputLabel>
              <Select
                label="Frequency"
                name="frequency"
                value={editGroup.frequency || 'weekly'}
                onChange={e => setEditGroup(prev => ({ ...prev, frequency: e.target.value }))}
                style={{ background: '#181f2a', color: '#fff', borderRadius: 8 }}
                labelId="frequency-label"
                disabled={!editGroup.recurring}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Every 2 weeks</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="edit-form-row">
            <label style={{ color: '#9fa8da', marginBottom: 4 }}>Schedule</label>
            {editSchedules.map((sch, idx) => (
              <div key={idx} className="schedule-row-modern">
                <FormControl style={{ minWidth: 120, marginRight: 8 }}>
                  <Select
                    value={sch.day || ''}
                    onChange={e => handleScheduleChange(idx, 'day', e.target.value)}
                    style={{ background: '#181f2a', color: '#fff', borderRadius: 8 }}
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <MenuItem key={day} value={day}>{day}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  type="time"
                  value={sch.time || ''}
                  onChange={e => handleScheduleChange(idx, 'time', e.target.value)}
                  style={{ marginRight: 8, background: '#181f2a', color: '#fff', borderRadius: 8 }}
                  InputProps={{ style: { color: '#fff' } }}
                />
                <TextField
                  type="number"
                  value={sch.duration || 60}
                  onChange={e => handleScheduleChange(idx, 'duration', e.target.value)}
                  style={{ width: 80, marginRight: 8, background: '#181f2a', color: '#fff', borderRadius: 8 }}
                  InputProps={{ style: { color: '#fff' }, min: 1 }}
                />
                <IconButton aria-label="remove" onClick={() => handleRemoveSchedule(idx)} size="small" style={{ color: '#e57373' }}>
                  <CancelIcon />
                </IconButton>
              </div>
            ))}
            <Button variant="outlined" onClick={handleAddSchedule} style={{ marginTop: 8, color: '#fff', borderColor: '#3949ab', background: '#222b3a' }}>Add Schedule</Button>
          </div>
          <div className="edit-form-actions">
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              style={{ marginRight: 12, background: '#3949ab', color: '#fff', borderRadius: 8 }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={() => setEditMode(false)}
              disabled={saving}
              style={{ color: '#fff', borderColor: '#3949ab', background: '#222b3a', borderRadius: 8 }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutTab;